import { useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { Icon } from './Icon'

export interface Toast { id: number; msg: string; tone?: string; code?: string | null; action?: { label: string; fn: () => void } }

// Dica inline (onboarding) dispensável: o operador aprende o modelo uma vez e some.
// `id` chaveia a persistência (localStorage) para a dica não voltar.
export function Legend({ id, children }: { id: string; children: ReactNode }) {
  const key = 'fz-legend-' + id
  const [hidden, setHidden] = useState<boolean>(() => { try { return localStorage.getItem(key) === '1' } catch { return false } })
  if (hidden) {return null}
  return (
    <div className="fz-legend">
      <Icon name="info" size={15} />
      <span>{children}</span>
      <button className="fz-legend__x" aria-label="Ocultar dica" onClick={() => { try { localStorage.setItem(key, '1') } catch { /* ignore */ } setHidden(true) }}>
        <Icon name="xPlain" size={13} />
      </button>
    </div>
  )
}

export function Empty({ icon = 'inbox', title, sub }: { icon?: string; title: string; sub?: string }) {
  return (
    <div className="fz-empty">
      <Icon name={icon} size={30} />
      <div className="fz-empty__title">{title}</div>
      {sub && <div className="fz-empty__sub">{sub}</div>}
    </div>
  )
}

function formatToastMsg(msg: unknown): string {
  if (!msg) {return ''}
  if (typeof msg === 'string') {return msg}
  if (msg instanceof Error) {return msg.message}
  if (typeof msg === 'object') {
    const o = msg as Record<string, unknown>
    return (o.message as string) || (o.error as string) || (o.detail as string) || JSON.stringify(msg)
  }
  return String(msg)
}

function formatToastCode(code: unknown): string {
  if (!code) {return ''}
  if (typeof code === 'string') {return code}
  if (typeof code === 'object') {
    const o = code as Record<string, unknown>
    return (o.code as string) || (o.id as string) || JSON.stringify(code)
  }
  return String(code)
}

export function Toasts({ toasts }: { toasts: Toast[] }) {
  return (
    <div className="fz-toasts">
      {toasts.map((t) => (
        <div key={t.id} role={t.tone === 'error' ? 'alert' : 'status'} aria-live={t.tone === 'error' ? 'assertive' : 'polite'} className={`fz-toast fz-toast--${t.tone || 'success'}`}>
          <Icon name={t.tone === 'error' ? 'alert' : t.tone === 'warning' ? 'alert' : 'check'} size={18} />
          <span>{formatToastMsg(t.msg)}{t.code && <> <code>{formatToastCode(t.code)}</code></>}</span>
          {t.action && <button className="fz-toast__action" onClick={t.action.fn}>{t.action.label}</button>}
          {t.action && <div className="fz-toast__countdown" />}
        </div>
      ))}
    </div>
  )
}

export function Counter({ n, label, tone = 'neutral' }: { n: number; label: string; tone?: string }) {
  return (
    <div>
      <div className={`fz-counter__n${tone !== 'neutral' ? ` fz-counter__n--${tone}` : ''}`}>{n}</div>
      <div className="fz-counter__label">{label}</div>
    </div>
  )
}

function renderValue(v: ReactNode) {
  if (typeof v !== 'string') {return v}
  const regex = /(?:https?|ftp):\/\/[^\s]+/gi
  if (!regex.test(v)) {return v}
  
  const parts = v.split(regex)
  const matches = v.match(regex) || []
  const result: ReactNode[] = []
  
  for (let i = 0; i < parts.length; i++) {
    result.push(parts[i])
    if (i < matches.length) {
      result.push(<a key={i} target="_blank" rel="noreferrer" href={matches[i]} style={{ textDecoration: 'underline', color: 'var(--color-primary)' }}>{matches[i]}</a>)
    }
  }
  return result
}

export function KV({ k, v, mono }: { k: ReactNode; v: ReactNode; mono?: boolean }) {
  return (
    <div className="fz-kv__row">
      <span className="fz-kv__k">{k}</span>
      <span className={'fz-kv__v ' + (mono ? 'mono' : '')}>{renderValue(v)}</span>
    </div>
  )
}

export type { CSSProperties }
